import { apiRequest, apiUpload, api } from './api'
import { Document, DocumentType, DocumentStatus, UserDocument } from '../types'

export const documentsService = {
  // Upload document
  async uploadDocument(formData: FormData): Promise<Document> {
    const response = await apiUpload<Document>('/documents/upload', formData)
    return response.data
  },

  // Get documents for a case
  async getCaseDocuments(caseId: string): Promise<Document[]> {
    const response = await apiRequest.get<Document[]>(`/documents/case/${caseId}`)
    return response.data
  },

  // Get document checklist for a case
  async getDocumentChecklist(caseId: string): Promise<any[]> {
    const response = await apiRequest.get<any[]>(`/documents/case/${caseId}/checklist`)
    return response.data
  },

  // Get documents summary for a case
  async getCaseDocumentsSummary(caseId: string): Promise<any> {
    const response = await apiRequest.get<any>(`/documents/case/${caseId}/summary`)
    return response.data
  },

  // Check if all required documents are verified
  async verifyAllRequiredDocuments(caseId: string): Promise<any> {
    const response = await apiRequest.get<any>(`/documents/case/${caseId}/verify-required`)
    return response.data
  },

  // Get document by ID
  async getDocumentById(id: string): Promise<Document> {
    const response = await apiRequest.get<Document>(`/documents/${id}`)
    return response.data
  },

  // Download document
  async downloadDocument(id: string): Promise<Blob> {
    const response = await api.get(`/documents/${id}/download`, {
      responseType: 'blob',
    })

    return response.data
  },

  // Update document status
  async updateDocumentStatus(id: string, status: DocumentStatus, verificationNotes?: string): Promise<Document> {
    const response = await apiRequest.patch<Document>(`/documents/${id}/status`, {
      status,
      verificationNotes,
    })
    return response.data
  },

  // Verify document (admin only)
  async verifyDocument(id: string, verificationNotes?: string): Promise<Document> {
    const response = await apiRequest.patch<Document>(`/documents/${id}/verify`, {
      verificationNotes,
    })
    return response.data
  },

  // Reject document (admin only)
  async rejectDocument(id: string, verificationNotes?: string): Promise<Document> {
    const response = await apiRequest.patch<Document>(`/documents/${id}/reject`, {
      verificationNotes,
    })
    return response.data
  },

  // Verify provider document (admin only)
  async verifyProviderDocument(id: string, verificationNotes?: string): Promise<Document> {
    const response = await apiRequest.patch<Document>(`/documents/provider/${id}/verify`, {
      verificationNotes,
    })
    return response.data
  },

  // Reject provider document (admin only)
  async rejectProviderDocument(id: string, verificationNotes?: string): Promise<Document> {
    const response = await apiRequest.patch<Document>(`/documents/provider/${id}/reject`, {
      verificationNotes,
    })
    return response.data
  },

  // Delete document
  async deleteDocument(id: string): Promise<{ message: string }> {
    const response = await apiRequest.delete<{ message: string }>(`/documents/${id}`)
    return response.data
  },

  // Admin methods
  async getAdminPendingDocuments(): Promise<Document[]> {
    const response = await apiRequest.get<Document[]>('/documents/admin/pending')
    return response.data
  },

  async getAdminAllDocuments(): Promise<Document[]> {
    const response = await apiRequest.get<Document[]>('/documents/admin/all')
    return response.data
  },

  // Provider document methods
  async getProviderDocuments(): Promise<Document[]> {
    const response = await apiRequest.get<Document[]>('/documents/provider/documents')
    return response.data
  },

  async uploadProviderDocument(formData: FormData): Promise<Document> {
    const response = await apiUpload<Document>('/documents/provider/documents/upload', formData)
    return response.data
  },

  async deleteProviderDocument(id: string): Promise<{ message: string }> {
    const response = await apiRequest.delete<{ message: string }>(`/documents/provider/documents/${id}`)
    return response.data
  },

  async downloadProviderDocument(id: string): Promise<Blob> {
    const response = await api.get(`/documents/provider/${id}/download`, {
      responseType: 'blob',
    })

    return response.data
  },

  // User (family) document methods
  async getUserDocuments(): Promise<UserDocument[]> {
    const response = await apiRequest.get<UserDocument[]>('/documents/user/documents')
    return response.data
  },

  async uploadUserDocument(formData: FormData): Promise<UserDocument> {
    const response = await apiUpload<UserDocument>('/documents/user/documents/upload', formData)
    return response.data
  },

  async deleteUserDocument(id: string): Promise<{ message: string }> {
    const response = await apiRequest.delete<{ message: string }>(`/documents/user/documents/${id}`)
    return response.data
  },

  async verifyUserDocument(id: string, verificationNotes?: string): Promise<UserDocument> {
    const response = await apiRequest.patch<UserDocument>(`/documents/user/${id}/verify`, {
      verificationNotes,
    })
    return response.data
  },

  async rejectUserDocument(id: string, verificationNotes?: string): Promise<UserDocument> {
    const response = await apiRequest.patch<UserDocument>(`/documents/user/${id}/reject`, {
      verificationNotes,
    })
    return response.data
  },

  async downloadUserDocument(id: string): Promise<Blob> {
    const response = await api.get(`/documents/user/${id}/download`, {
      responseType: 'blob',
    })
    return response.data
  },

  // Admin user document methods
  async getAdminPendingUserDocuments(): Promise<UserDocument[]> {
    const response = await apiRequest.get<UserDocument[]>('/documents/admin/user-pending')
    return response.data
  },

  async getAdminAllUserDocuments(): Promise<UserDocument[]> {
    const response = await apiRequest.get<UserDocument[]>('/documents/admin/user-all')
    return response.data
  },
}